import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';

const CustomerSearch = ({ onSelect, loading: externalLoading, selectedCustomerId, customersData }) => {
   
  const [searchTerm, setSearchTerm] = useState('');
  const [customers, setCustomers] = useState([]);
  const [showCustomerList, setShowCustomerList] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [internalLoading, setInternalLoading] = useState(false);
  
  const customerListRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const customerItemsRef = useRef([]);
;
  // Scroll to highlighted item
  useEffect(() => {
    if (highlightIndex >= 0 && customerListRef.current && showCustomerList) {
      setTimeout(() => {
        const container = customerListRef.current;
        const highlightedElement = customerItemsRef.current[highlightIndex];
        
        if (highlightedElement && container) {
          const itemRect = highlightedElement.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          
          if (itemRect.bottom > containerRect.bottom) {
            container.scrollTop += (itemRect.bottom - containerRect.bottom) + 10;
          } else if (itemRect.top < containerRect.top) {
            container.scrollTop -= (containerRect.top - itemRect.top) + 10;
          }
        }
      }, 0);
    }
  }, [highlightIndex, showCustomerList]);

  // Fetch selected customer name if selectedCustomerId is provided
  useEffect(() => {
    if (selectedCustomerId && selectedCustomerId !== '' && !searchTerm) {
      const setCustomerFromLocalData = () => {
        try {
         
          const customer = customersData.find(cust => 
            cust.user_id === selectedCustomerId || cust.user_id === selectedCustomerId
          );
          
          if (customer) {
            setSearchTerm(customer.name);
          } else {
            console.warn(`Customer with ID ${selectedCustomerId} not found in local data`);
          }
        } catch (error) {
          console.error('Failed to find customer in local data:', error);
        }
      };
      
      setCustomerFromLocalData();
    }
  }, [selectedCustomerId, searchTerm, customersData]);

  // Auto highlight first item when customers list updates
  useEffect(() => {
    if (customers.length > 0 && showCustomerList) {
      setHighlightIndex(0);
      if (customerListRef.current) {
        customerListRef.current.scrollTop = 0;
      }
    } else {
      setHighlightIndex(-1);
    }
  }, [customers, showCustomerList]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowCustomerList(false);
      }
    };

    if (showCustomerList) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showCustomerList]);

  const fetchCustomers = useCallback(async () => {
    if (!searchTerm.trim()) {
      setCustomers([]);
      return;
    }
    
    setInternalLoading(true);
    try {
      const filteredCustomers = customersData.filter(customer => {
        const searchLower = searchTerm.toLowerCase();
        return (
          customer.name?.toLowerCase().includes(searchLower) ||
          customer.email?.toLowerCase().includes(searchLower) ||
          customer.phone?.includes(searchTerm) ||
          customer.user_id?.toString().includes(searchTerm)
        );
      });
      
      setCustomers(filteredCustomers || []);
    } catch (error) {
      toast.error('Error filtering customers');
      setCustomers([]);
    } finally {
      setInternalLoading(false);
    }
  }, [searchTerm, customersData]);

  const handleKeyDown = (e) => {
    if (!customers.length && e.key !== 'Escape') return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (showCustomerList && customers.length > 0) {
          setHighlightIndex((prev) => {
            const newIndex = prev < customers.length - 1 ? prev + 1 : 0;
            return newIndex;
          });
        }
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        if (showCustomerList && customers.length > 0) {
          setHighlightIndex((prev) => {
            const newIndex = prev > 0 ? prev - 1 : customers.length - 1;
            return newIndex;
          });
        }
        break;
        
      case 'Enter':
        e.preventDefault();
        if (highlightIndex >= 0 && customers[highlightIndex]) {
          handleCustomerSelect(customers[highlightIndex]);
        }
        break;
        
      case 'Escape':
        setShowCustomerList(false);
        inputRef.current?.blur();
        break;
        
      default:
        break;
    }
  };

  const handleCustomerSelect = (customer) => {
    setSearchTerm(customer.name);
    setShowCustomerList(false);
    setCustomers([]);
    setHighlightIndex(-1);
    
    if (onSelect) {
      onSelect(customer.user_id, customer.name, customer.phone);
    }
  };

  const handleMouseEnter = (index) => {
    setHighlightIndex(index);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    if (value.length > 0) {
      setShowCustomerList(true);
    } else {
      setCustomers([]);
      setShowCustomerList(false);
      
      if (onSelect) {
        onSelect('', '');
      }
    }
  };

  const handleInputFocus = () => {
    if (searchTerm.length > 0) {
      setShowCustomerList(true);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      if (!containerRef.current?.matches(':hover')) {
        setShowCustomerList(false);
      }
    }, 150);
  };

  const handleClearInput = () => {
    setSearchTerm('');
    setCustomers([]);
    setShowCustomerList(false);
    
    if (onSelect) {
      onSelect('', '');
    }
    
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleListWheel = (e) => {
    e.stopPropagation();
  };

  useEffect(() => {
    customerItemsRef.current = customerItemsRef.current.slice(0, customers.length);
  }, [customers]);

  useEffect(() => {
    if (searchTerm.length > 0 && showCustomerList) {
      const timer = setTimeout(() => {
        fetchCustomers();
      }, 300);
      return () => clearTimeout(timer);
    } else if (!searchTerm) {
      setCustomers([]);
    }
  }, [searchTerm, showCustomerList, fetchCustomers]);

  const isLoading = internalLoading || externalLoading;

  // CSS styles object
  const styles = {
    container: {
      position: 'relative'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '4px'
    },
    inputWrapper: {
      position: 'relative'
    },
    input: {
      width: '100%',
      padding: '8px 14px',
      borderStyle: 'solid',
      borderRadius: '8px',
      outline: 'none',
      transition: 'all 0.2s ease',
      boxSizing: 'border-box'
    },
    inputFocus: {
      borderColor: '#f90476ff',
      boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.2)'
    },
    clearButton: {
      position: 'absolute',
      right: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#b606a2ff',
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      padding: '0',
      zIndex: 10
    },
    clearButtonHover: {
      color: '#4b5563'
    },
    clearIcon: {
      width: '16px',
      height: '16px',
      display: 'block'
    },
    dropdown: {
      position: 'absolute',
      zIndex: 9999,
      marginTop: '4px',
      width: '100%',
      backgroundColor: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
      maxHeight: '288px',
      overflowY: 'auto'
    },
    scrollbar: {
      scrollbarWidth: 'thin',
      scrollbarColor: 'rgba(156, 163, 175, 0.5) transparent'
    },
    loadingContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px'
    },
    spinner: {
      animation: 'spin 1s linear infinite',
      borderRadius: '50%',
      height: '24px',
      width: '24px',
      border: '2px solid #3b82f6',
      borderTopColor: 'transparent'
    },
    loadingText: {
      marginLeft: '12px',
      fontSize: '14px',
      color: '#4b5563'
    },
    customersList: {
      padding: '4px 0'
    },
    customerItem: {
      padding: '12px 16px',
      cursor: 'pointer',
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      borderLeftStyle: 'solid'
    },
    customerItemHover: {
      backgroundColor: '#f9fafb'
    },
    customerItemHighlighted: {
      background: 'linear-gradient(to right, rgba(59, 130, 246, 0.05), rgba(59, 130, 246, 0.1))',
      borderLeftColor: '#3b82f6',
      color: '#1e40af',
      transform: 'translateX(2px)'
    },
    customerName: {
      fontWeight: '500',
      fontSize: '14px'
    },
    customerInfo: {
      display: 'flex',
      alignItems: 'center',
      marginTop: '4px',
      gap: '12px'
    },
    phoneIcon: {
      width: '12px',
      height: '12px',
      marginRight: '4px'
    },
    phoneText: {
      fontSize: '12px',
      color: '#6b7280',
      display: 'flex',
      alignItems: 'center'
    },
    noResultsContainer: {
      padding: '24px',
      textAlign: 'center'
    },
    noResultsIcon: {
      width: '48px',
      height: '48px',
      color: '#9ca3af',
      margin: '0 auto 8px'
    },
    noResultsText: {
      color: '#6b7280',
      fontWeight: '500',
      marginBottom: '4px'
    },
    searchTermHighlight: {
      fontWeight: '600'
    }
  };

  // CSS as a string for style tag
  const customCSS = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    
    .customer-search-dropdown::-webkit-scrollbar {
      width: 6px;
    }
    
    .customer-search-dropdown::-webkit-scrollbar-track {
      background: transparent;
      border-radius: 10px;
    }
    
    .customer-search-dropdown::-webkit-scrollbar-thumb {
      background: rgba(156, 163, 175, 0.5);
      border-radius: 10px;
      transition: all 0.3s ease;
    }
    
    .customer-search-dropdown::-webkit-scrollbar-thumb:hover {
      background: rgba(156, 163, 175, 0.7);
    }
    
    .customer-search-dropdown {
      -webkit-overflow-scrolling: touch;
    }
  `;

  return (
    <div style={styles.container} ref={containerRef}>
      <style>{customCSS}</style>
     
      
      <div style={styles.inputWrapper}>
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder="Search by name or phone"
          style={{
            ...styles.input,
            ...(document.activeElement === inputRef.current ? styles.inputFocus : {})
          }}
          autoComplete="off"
          disabled={externalLoading}
        />
        
        {searchTerm && (
          <button
            type="button"
            onClick={handleClearInput}
            style={styles.clearButton}
            onMouseOver={(e) => e.currentTarget.style.color = styles.clearButtonHover.color}
            onMouseOut={(e) => e.currentTarget.style.color = styles.clearButton.color}
            disabled={externalLoading}
          >
            <svg style={styles.clearIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
      
      {showCustomerList && (
        <div 
          ref={customerListRef}
          style={{...styles.dropdown, ...styles.scrollbar}}
          className="customer-search-dropdown"
          onWheel={handleListWheel}
        >
          {isLoading ? (
            <div style={styles.loadingContainer}>
              <div style={styles.spinner}></div>
              <span style={styles.loadingText}>Searching...</span>
            </div>
          ) : customers.length > 0 ? (
            <div style={styles.customersList}>
              {customers.map((customer, idx) => (
                <div
                  key={customer.user_id}
                  ref={el => customerItemsRef.current[idx] = el}
                  style={{
                    ...styles.customerItem,
                    ...(idx === highlightIndex 
                      ? styles.customerItemHighlighted 
                      : {})
                  }}
                  onMouseEnter={() => handleMouseEnter(idx)}
                  onMouseOver={(e) => {
                    if (idx !== highlightIndex) {
                      e.currentTarget.style.backgroundColor = styles.customerItemHover.backgroundColor;
                    }
                  }}
                  onMouseOut={(e) => {
                    if (idx !== highlightIndex) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                  onClick={() => handleCustomerSelect(customer)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div style={styles.customerName}>{customer.name}</div>
                    <div style={styles.customerInfo}>
                      <span style={styles.phoneText}>
                        <svg style={styles.phoneIcon} fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                        </svg>
                        {customer.phone}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : searchTerm.length > 0 && !isLoading ? (
            <div style={styles.noResultsContainer}>
              <div style={styles.noResultsIcon}>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <p style={styles.noResultsText}>No customers found</p>
              <p style={{ color: '#9ca3af', fontSize: '14px', marginTop: '4px' }}>
                for "<span style={styles.searchTermHighlight}>{searchTerm}</span>"
              </p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default CustomerSearch;